import React, { useState, useRef, useEffect } from 'react'
import Map, { Marker } from 'react-map-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { useMutation, useQuery, gql } from '@apollo/client'
import {
  updateDeliveryBoundsAndLocation,
  getRestaurantProfile,
} from '../apollo'
import ConfigurableValues from '../config/constants'
import {
  Container, Box, Button, Paper, Stack, Typography, Snackbar, Alert,
  CircularProgress,
} from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import SaveIcon from '@mui/icons-material/Save'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import { useTranslation, withTranslation } from 'react-i18next'

const UPDATE = gql`${updateDeliveryBoundsAndLocation}`
const GET_RESTAURANT_PROFILE = gql`${getRestaurantProfile}`

function RestaurantLocation() {
  const { t } = useTranslation()
  const { MAPBOX_TOKEN } = ConfigurableValues()
  const restaurantId = localStorage.getItem('restaurantId')

  const [center, setCenter] = useState({ lat: 42.475, lng: -71.099 })
  const [marker, setMarker] = useState(null)
  const [snack, setSnack] = useState({ open: false, severity: 'success', msg: '' })
  const mapRef = useRef(null)

  // Recenter the map when `center` changes (profile load / "use my location")
  useEffect(() => {
    if (mapRef.current && center) {
      mapRef.current.easeTo({ center: [center.lng, center.lat], zoom: 14, duration: 400 })
    }
  }, [center])

  const { loading: loadingQuery } = useQuery(GET_RESTAURANT_PROFILE, {
    variables: { id: restaurantId },
    fetchPolicy: 'network-only',
    onCompleted: ({ restaurant }) => {
      if (restaurant?.location?.coordinates?.length >= 2) {
        const p = {
          lat: +restaurant.location.coordinates[1],
          lng: +restaurant.location.coordinates[0],
        }
        setCenter(p)
        setMarker(p)
      }
    },
  })

  const [mutate, { loading }] = useMutation(UPDATE, {
    onCompleted: () => setSnack({ open: true, severity: 'success', msg: t('LocationUpdatedSuccessfully') || 'Restaurant location saved' }),
    onError: () => setSnack({ open: true, severity: 'error', msg: t('UpdatingLocationError') || 'Failed to save location' }),
  })

  const onMapClick = (e) => {
    setMarker({ lat: e.lngLat.lat, lng: e.lngLat.lng })
  }
  const onMarkerDragEnd = (e) => {
    setMarker({ lat: e.lngLat.lat, lng: e.lngLat.lng })
  }

  const useDeviceLocation = () => {
    if (!navigator.geolocation) {
      setSnack({ open: true, severity: 'error', msg: 'Geolocation not supported in this browser' })
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude }
        setMarker(p); setCenter(p)
      },
      () => setSnack({ open: true, severity: 'error', msg: 'Could not access device location' }),
    )
  }

  const handleSave = () => {
    if (!marker) {
      setSnack({ open: true, severity: 'error', msg: 'Drop a pin on the map first' })
      return
    }
    mutate({
      variables: {
        id: restaurantId,
        boundType: 'Polygon',
        location: { latitude: marker.lat, longitude: marker.lng },
        address: 'nil',
        circleBounds: { radius: 0.0 },
      },
    })
  }

  const dateStr = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })

  return (
    <>
      {/* Hero banner */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)',
        px: { xs: 2, md: 4 }, pt: 3.5, pb: 5,
        position: 'relative', overflow: 'hidden',
        '&::before': { content: '""', position: 'absolute', top: -60, right: -60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' },
      }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1, flexWrap: 'wrap' }}>
          <Box sx={{
            width: 52, height: 52, borderRadius: '14px',
            background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <LocationOnIcon sx={{ color: '#93c5fd', fontSize: 26 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#fff', lineHeight: 1.15, letterSpacing: '-0.02em' }}>
              Restaurant Location
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', mt: 0.3 }}>
              {dateStr} &middot; Pin your storefront so customers and drivers can find you
            </Typography>
          </Box>
          <Stack direction="row" gap={1.5}>
            <Button
              variant="outlined"
              startIcon={<MyLocationIcon />}
              onClick={useDeviceLocation}
              sx={{
                color: '#fff', borderColor: 'rgba(255,255,255,0.4)',
                textTransform: 'none', fontWeight: 700, borderRadius: '12px', px: 2,
                '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
            >
              Use my location
            </Button>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={loading}
              sx={{
                backgroundColor: '#fff', color: '#1e40af', textTransform: 'none', fontWeight: 700,
                borderRadius: '12px', px: 2.5, py: 1, boxShadow: '0 6px 14px rgba(0,0,0,0.18)',
                '&:hover': { backgroundColor: '#f1f5f9' },
              }}
            >
              {loading ? 'Saving…' : 'Save Location'}
            </Button>
          </Stack>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
        {/* Coordinates info */}
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} sx={{ mb: 2.5 }}>
          <Paper elevation={0} sx={{ flex: 1, p: 2.5, borderRadius: '16px', border: '1px solid #e5e7eb' }}>
            <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>Latitude</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontSize: '1.1rem', fontWeight: 700, color: marker ? '#111827' : '#9ca3af', mt: 0.5 }}>
              {marker ? marker.lat.toFixed(6) : '—'}
            </Typography>
          </Paper>
          <Paper elevation={0} sx={{ flex: 1, p: 2.5, borderRadius: '16px', border: '1px solid #e5e7eb' }}>
            <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>Longitude</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontSize: '1.1rem', fontWeight: 700, color: marker ? '#111827' : '#9ca3af', mt: 0.5 }}>
              {marker ? marker.lng.toFixed(6) : '—'}
            </Typography>
          </Paper>
          <Paper elevation={0} sx={{ flex: 2, p: 2.5, borderRadius: '16px', border: '1px solid #e5e7eb', backgroundColor: '#f8fafc' }}>
            <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>Tip</Typography>
            <Typography sx={{ color: '#475569', fontSize: '0.85rem', mt: 0.5, lineHeight: 1.5 }}>
              <Box component="span" sx={{ fontWeight: 700, color: '#1e40af' }}>Click</Box> anywhere on the map to drop the pin, or <Box component="span" sx={{ fontWeight: 700, color: '#1e40af' }}>drag</Box> the existing pin to fine-tune. Use the green button to snap to your current location.
            </Typography>
          </Paper>
        </Stack>

        {/* Map */}
        <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          {loadingQuery ? (
            <Box sx={{ height: 540, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress sx={{ color: '#818cf8' }} />
            </Box>
          ) : (
            <Map
              ref={mapRef}
              mapboxAccessToken={MAPBOX_TOKEN}
              initialViewState={{ longitude: center.lng, latitude: center.lat, zoom: 14 }}
              style={{ height: '540px', width: '100%' }}
              mapStyle="mapbox://styles/mapbox/streets-v12"
              onClick={onMapClick}
            >
              {marker && (
                <Marker
                  longitude={marker.lng}
                  latitude={marker.lat}
                  anchor="bottom"
                  draggable
                  onDragEnd={onMarkerDragEnd}
                >
                  <LocationOnIcon sx={{ color: '#D32F2F', fontSize: 40 }} />
                </Marker>
              )}
            </Map>
          )}
        </Paper>
      </Container>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  )
}

export default withTranslation()(RestaurantLocation)
